import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Star } from 'lucide-react';
import { SatisfactionMetrics as SatisfactionData } from '@/hooks/useDashboardMetrics';

interface CustomerSatisfactionProps {
  data: SatisfactionData | undefined;
  isLoading: boolean;
}

const CustomerSatisfaction = ({ data, isLoading }: CustomerSatisfactionProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        </CardHeader>
        <CardContent>
          <div className="text-center animate-pulse">
            <div className="h-12 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const getNPSColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getNPSBadge = (score: number) => {
    if (score >= 8) return { text: 'Excelente', variant: 'default' as const, color: 'bg-green-100 text-green-800' };
    if (score >= 6) return { text: 'Bom', variant: 'secondary' as const, color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Precisa Melhorar', variant: 'destructive' as const, color: 'bg-red-100 text-red-800' };
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`h-4 w-4 ${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  const npsStatus = getNPSBadge(data.npsScore);

  return (
    <Card className="hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-yellow-600">
          <Star className="h-5 w-5" />
          Satisfação do Cliente
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center">
          {/* NPS Score */}
          <div className={`text-4xl font-bold mb-2 ${getNPSColor(data.npsScore)}`}>
            {data.npsScore}
          </div>
          <p className="text-sm text-gray-600 mb-3">NPS Score</p>
          
          {/* Badge de Status */}
          <Badge className={`mb-4 ${npsStatus.color}`}>
            {npsStatus.text}
          </Badge>

          {/* Avaliação por Estrelas */}
          <div className="flex justify-center mb-3">
            {renderStars(data.customerRating)}
          </div>
          <p className="text-xs text-gray-500 mb-4">
            {data.customerRating}/5.0 - Avaliação média
          </p>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span>Meta: 8.0</span>
              <span className={data.npsScore >= 8 ? 'text-green-600' : 'text-red-600'}>
                {data.npsScore >= 8 ? '✓ Meta atingida' : '⚠ Abaixo da meta'}
              </span>
            </div>
            <Progress value={(data.npsScore / 10) * 100} className="h-2" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomerSatisfaction;
